import { createFileRoute } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { loadMarketCatalog, type MarketCatalogResult } from "@/lib/market-catalog";
import { renderMarketPublicOffline } from "@/lib/market-public-offline-gate";
import { MarketPageSkeleton } from "@/components/market/MarketSkeletons";
import { ServiceBookingWidget } from "@/components/market/ServiceBookingWidget";

const fetchCatalog = createServerFn({ method: "GET" })
  .inputValidator((slug: string) => slug)
  // @ts-expect-error ServerFnCtx framework type mismatch
  .handler(async ({ data: slug }) => loadMarketCatalog(slug, { request: getRequest() }));

export const Route = createFileRoute("/market_/$slug/turno")({
  pendingComponent: MarketPageSkeleton,
  pendingMs: 0,
  validateSearch: (s: Record<string, unknown>) => ({
    service: typeof s.service === "string" ? s.service : undefined,
  }),
  loader: async ({ params }) => fetchCatalog({ data: params.slug }),
  component: TurnoPage,
  head: ({ params, loaderData }) => {
    const result = loaderData as MarketCatalogResult | undefined;
    const name = result?.ok ? result.data.tenant.name : params.slug;
    return {
      meta: [
        { title: `Reservar en ${name}` },
        { name: "description", content: `Reservá tu turno online en ${name}` },
      ],
    };
  },
});

function TurnoPage() {
  const { slug } = Route.useParams();
  const { service } = Route.useSearch();
  const result = Route.useLoaderData() as MarketCatalogResult;
  const offline = renderMarketPublicOffline(result);
  if (offline) return offline;
  if (!result.ok || !result.data.showServiceBookings) {
    return (
      <div className="flex min-h-dvh items-center justify-center p-8 text-center text-sm text-muted-foreground">
        Este comercio no tiene reservas de servicio habilitadas.
      </div>
    );
  }
  const { tenant, config, customerAccountsEnabled, experiencePublished } = result.data;
  // La experiencia publicada manda sobre config: es la misma fuente que pinta el storefront.
  const brand = experiencePublished?.brand?.colors;
  return (
    <ServiceBookingWidget
      slug={slug}
      preselectedServiceId={service}
      branding={{
        tenantName: tenant.name,
        slug,
        description: tenant.description,
        logoUrl: tenant.logoUrl,
        heroImageUrl: config.heroImageUrl,
        phone: tenant.phone,
        whatsappNumber: config.whatsappNumber,
        address: tenant.address,
        primaryColor: brand?.primary || config.primaryColor,
        secondaryColor: brand?.accent || config.secondaryColor,
        backgroundColor: brand?.background || config.backgroundColor,
        surfaceColor: brand?.surface,
        textColor: brand?.text,
        textMutedColor: brand?.textMuted,
        borderColor: brand?.border,
        customerAccountsEnabled,
      }}
    />
  );
}
